import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { StringField } from '../StringField'
import { useTranslation } from 'react-i18next'
import { useConfigs } from '../../context/configs_context'
import { WhatsappConfirmationContext } from '../../context/password_context'
import {
    getMasterPasswordWhatsappStatus,
    markWhatsappValidated,
    sendMasterPasswordWhatsapp,
} from '../../services/configs'

interface Props {
    open: boolean
    handleClose(v: boolean): void
    confirmPassword: boolean
    returnPassword(password: string): void
    err: string
    setErr(v: string): void
    customDescription?: () => JSX.Element
    whatsappContext?: WhatsappConfirmationContext
}

export const PasswordModal = ({
    open,
    handleClose,
    confirmPassword,
    returnPassword,
    err,
    setErr,
    customDescription,
    whatsappContext,
}: Props) => {
    const { t } = useTranslation()
    const { configs } = useConfigs()
    const [pass, setPass] = useState<string>('')
    const [confPass, setConfPass] = useState<string>('')
    const [whatsappWaiting, setWhatsappWaiting] = useState(false)
    const [whatsappToken, setWhatsappToken] = useState<string | null>(null)
    const [sending, setSending] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [step, setStep] = useState(0)
    const ref = useRef<HTMLInputElement | null>(null)

    const eventListener = (e: KeyboardEvent) => {
        if (e.key === 'Enter') buttonRef.current?.click()
    }

    const canUseWhatsapp =
        !confirmPassword &&
        configs.whatsappConfirmationEnabled &&
        !!configs.whatsappPhone

    useEffect(() => {
        setPass('')
        setConfPass('')
        setWhatsappWaiting(false)
        setWhatsappToken(null)
        setStep(0)
        setErr('')

        document.body.addEventListener('keydown', eventListener)

        setTimeout(() => {
            ref.current?.focus()
        }, 100)

        return () => {
            document.body.removeEventListener('keydown', eventListener)
        }
    }, [open, confirmPassword])

    useEffect(() => {
        setErr(err)
    }, [err])

    useEffect(() => {
        if (step === 1 && pass !== confPass) setErr('As senhas não são iguais!')
        else setErr('')
    }, [pass, confPass])

    useEffect(() => {
        if (!whatsappWaiting || !whatsappToken) return

        let cancelled = false
        let attempts = 0

        const interval = setInterval(async () => {
            attempts += 1

            if (attempts > 20) {
                clearInterval(interval)
                setWhatsappWaiting(false)
                setErr(
                    'Não foi possível confirmar. Verifique o webhook e tente novamente.'
                )
                return
            }

            try {
                const { confirmed, expired } =
                    await getMasterPasswordWhatsappStatus(
                        whatsappToken as string
                    )

                if (cancelled) return

                if (confirmed) {
                    clearInterval(interval)
                    markWhatsappValidated()
                    setWhatsappWaiting(false)
                    returnPassword('whatsapp')
                } else if (expired) {
                    clearInterval(interval)
                    setWhatsappWaiting(false)
                    setErr('Confirmação expirada. Tente novamente.')
                }
            } catch (error) {
                // mantém aguardando; nova tentativa no próximo ciclo
            }
        }, 3000)

        return () => {
            cancelled = true
            clearInterval(interval)
        }
    }, [whatsappWaiting, whatsappToken])

    const validate = (): boolean => {
        if (!confirmPassword) {
            if (pass.length < 6) return false
        } else {
            if (pass.length < 6) return false
            if (confPass.length < 6) return false
            if (pass !== confPass) return false
        }

        return true
    }

    const handleSendWhatsapp = async () => {
        setSending(true)
        try {
            const { token } = await sendMasterPasswordWhatsapp(
                whatsappContext
            )
            setErr('')
            setWhatsappToken(token)
            setWhatsappWaiting(true)
        } catch (error) {
            setErr('Não foi possível enviar a confirmação')
        }
        setSending(false)
    }

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(false)}
            className="modal"
        >
            <DialogTitle>
                <Box
                    sx={{
                        color: 'text.primary',
                        fontSize: '20px',
                        fontWeight: '500',
                        textAlign: 'center',
                        borderBottom: '1px solid rgba(218, 218, 218, 0.7)',
                        paddingBottom: '10px',
                        minWidth: { xs: 'auto', sm: '350px' },
                    }}
                >
                    {whatsappWaiting
                        ? t('Confirmação por WhatsApp')
                        : t('Contraseña')}
                </Box>
            </DialogTitle>
            <DialogContent>
                {whatsappWaiting ? (
                    <>
                        <Typography textAlign={'center'} mb={'15px'}>
                            {t('Enviamos uma mensagem para')}{' '}
                            <strong>{configs.whatsappPhone}</strong>.{' '}
                            {t('Toque no botão "Aprobar" no WhatsApp.')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                my: '20px',
                            }}
                        >
                            <CircularProgress size={'40px'} />
                        </Box>
                        <Typography textAlign={'center'} fontSize={'13px'}>
                            {t('Aguardando confirmação...')}
                        </Typography>
                        <Button
                            sx={{
                                width: '100%',
                                textTransform: 'none',
                                mt: '20px',
                                p: '10px',
                                fontSize: '16px',
                            }}
                            variant="text"
                            onClick={() => {
                                setWhatsappWaiting(false)
                                setWhatsappToken(null)
                                setErr('')
                            }}
                        >
                            {t('Voltar')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography textAlign={'center'} mb={'10px'}>
                            {step === 0
                                ? customDescription
                                    ? customDescription()
                                    : t('Para continuar, insire a sua senha')
                                : t('Confirme a sua senha')}
                        </Typography>
                        <StringField
                            label="Contraseña"
                            maxLength={50}
                            value={step === 0 ? pass : confPass}
                            setValue={step === 0 ? setPass : setConfPass}
                            ref={ref}
                            sx={{
                                width: '100%',
                            }}
                            error={err}
                            onClick={() => setErr('')}
                            type="password"
                        />
                        <Button
                            sx={{
                                width: '100%',
                                textTransform: 'none',
                                mt: '20px',
                                p: '10px',
                                fontSize: '16px',
                            }}
                            variant="contained"
                            disabled={
                                step === 0 && confirmPassword
                                    ? pass.length < 6
                                    : !validate()
                            }
                            ref={buttonRef}
                            onClick={() => {
                                if (step === 0 && confirmPassword) {
                                    setStep(1)
                                    setTimeout(() => {
                                        ref.current?.focus()
                                    }, 100)
                                } else {
                                    returnPassword(pass)
                                }
                            }}
                        >
                            {t('Confirmar')}
                        </Button>
                        {canUseWhatsapp && (
                            <Button
                                sx={{
                                    width: '100%',
                                    textTransform: 'none',
                                    mt: '10px',
                                    p: '10px',
                                    fontSize: '16px',
                                }}
                                variant="text"
                                disabled={sending}
                                onClick={handleSendWhatsapp}
                            >
                                {t('Confirmar por WhatsApp')}
                            </Button>
                        )}
                        {step === 1 && (
                            <Button
                                sx={{
                                    width: '100%',
                                    textTransform: 'none',
                                    mt: '20px',
                                    p: '10px',
                                    fontSize: '16px',
                                }}
                                variant="text"
                                onClick={() => {
                                    setStep(0)
                                    setPass('')
                                    setConfPass('')
                                }}
                            >
                                {t('Voltar')}
                            </Button>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
